import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getReviews,
  createReviewToken,
  submitReview,
  moderateReview,
  validateReviewToken,
  uploadReviewImage,
} from "@/lib/api/reviews";
import type { ReviewStatus, SubmitReviewParams, UploadReviewImageParams } from "@/types/review";

export function useReviews(params: {
  artistId?: string;
  status?: ReviewStatus;
  limit?: number;
  enabled?: boolean;
} = {}) {
  const { enabled = true, ...queryParams } = params;

  return useQuery({
    queryKey: ["reviews", queryParams],
    queryFn: () => getReviews(queryParams),
    enabled,
  });
}

export function useApprovedReviews(limit = 5) {
  return useReviews({ status: "approved", limit });
}

export function useArtistReviews(artistId: string, enabled = true) {
  return useReviews({ artistId, status: "approved", enabled });
}

export function usePendingReviews() {
  return useReviews({ status: "pending" });
}

export function useValidateToken(artistSlug: string, token: string) {
  return useQuery({
    queryKey: ["review-token", artistSlug, token],
    queryFn: () => validateReviewToken(artistSlug, token),
    enabled: !!artistSlug && !!token,
    retry: false,
  });
}

export function useCreateReviewToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createReviewToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review-tokens"] });
    },
  });
}

export function useSubmitReview() {
  return useMutation({
    mutationFn: (params: SubmitReviewParams) => submitReview(params),
  });
}

export function useUploadReviewImage() {
  return useMutation({
    mutationFn: (params: UploadReviewImageParams) => uploadReviewImage(params),
  });
}

export function useModerateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reviewId,
      action,
      updates,
    }: {
      reviewId: string;
      action: "approve" | "reject";
      updates?: { reviewText?: string; adminNotes?: string };
    }) => moderateReview(reviewId, action, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
    },
  });
}
